import {
  ELSCommonConfig,
  ELSTokenHelper
} from '@els/els-ui-common-react';
import { get } from 'lodash';
import moment from 'moment';
import { convertIsbnsToNormalizedString } from '../../utilities/app.utilities';
import { scrubProps } from '../../utilities/analytics.utilities';
import {
  FALSE_VALUE,
  FEATURE_FLAG,
  TRUE_VALUE
} from '../../apis/eols-features-api/eols-features-api.constants';
import { UNKNOWN } from './app.constants';
import {
  getBooleanFromGroupFeatureFlagWithFallbackToGlobal
} from '../../utilities/featureFlag.utilities';
import { AppState } from './app-state.types';
import { OsmosisTokenDto } from '../../apis/sherpath-course-management-service/sherpath-course-management-service.dtos';
import {
  getTokenIsbns,
  getTokenIsbnsArray
} from './app-state.utilities';
import {
  getBrand,
  getOsmosisAppLinkContext,
  isBankBasedCourse,
  isIframeContext,
  isVSTChat,
} from '../../pages/ai-chat/ai-chat.utilities';
import { InstitutionDto } from '../../apis/eols-course-crud/eols-course-crud.dtos';
import {
  RecordObject
} from '../../models/common.models';

export const getUserEmailDomain = (appState: AppState): string => {
  const user = appState.eolsUser;
  const emailAddress = !user || !user.emailAddress ? ELSTokenHelper.getUserEmail() : user.emailAddress;
  if (!emailAddress) {
    return UNKNOWN;
  }
  const emailParts = emailAddress.split('@');
  if (!emailParts || emailParts.length !== 2) {
    return UNKNOWN;
  }
  return emailParts[1];
};

export const getIsTestUser = (appState: AppState): string => {
  const emailDomain = getUserEmailDomain(appState);
  if (emailDomain === UNKNOWN) {
    return UNKNOWN;
  }

  const _isTestUser = ELSCommonConfig.testUserEmailDomains.some((domain) => {
    return emailDomain.includes(domain);
  });
  return _isTestUser ? TRUE_VALUE : FALSE_VALUE;
};

const getConsumerKey = (appState: AppState): string => {
  return get(appState, 'currentCourse.consumerKey', '');
};
export const getDefaultNewRelicActionProps = (appState: AppState): RecordObject => {
  const { evolveUser } = appState;
  const institution = appState.currentCourse ? appState.currentCourse.institution : null;
  const props = {
    courseSectionId: appState.courseSectionId,
    eolsUserId: appState.userId,
    evolveUserId: evolveUser ? evolveUser.externalUserId : UNKNOWN,
    userRole: appState.userRole,
    isbns: getTokenIsbns(),
    isbnsSorted: convertIsbnsToNormalizedString(getTokenIsbnsArray()),
    institution: institution ? institution.name : null,
    userEmailDomain: getUserEmailDomain(appState),
    isTestUser: getIsTestUser(appState),
    isMasqueradeUser: ELSTokenHelper.isMasqueradeUser(),
    pathname: window.location.pathname,
    appVersion: window.getBuildVersion ? window.getBuildVersion() : null,
    consumerKey: getConsumerKey(appState),
    isBankBased: isBankBasedCourse(appState.entitlements),
    isFrame: isIframeContext(appState.appLinkData),
    isVSTChat: isVSTChat(appState),
    brand: getBrand(appState),
    osmosisProgram: getOsmosisAppLinkContext(appState?.appLinkData)?.program || null,
    ecrId: appState?.ecrId || null,
  };
  return scrubProps(props);
};

export const getIsbns = (state: AppState): string[] => {
  if (!state.tokenIsbns) {
    return null;
  }
  return state.tokenIsbns.split(',');
};

export const getInstitution = (appState: AppState): InstitutionDto => {
  if (!appState.currentCourse) {
    return null;
  }
  return appState.currentCourse.institution;
};

export const getIsMaintenance = (appState: AppState): boolean => {
  const value = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    appState.featureFlagsGrouped,
    FEATURE_FLAG.IS_SITE_DOWN_FOR_MAINTENANCE,
    appState.courseSectionId
  );
  return value === null ? false : value;
};

export const shouldGetNewOsmosisToken = (osmosisTokenDto: OsmosisTokenDto): boolean => {
  if (!osmosisTokenDto || !osmosisTokenDto.expires) {
    return true;
  }
  return moment.utc(osmosisTokenDto.expires).add({ hours: -10 }).isBefore(moment());
};
